import React from 'react';

import Head from 'next/head';

import { BaseCardTileList, ArticleCardTile } from 'styled';
import { ArticleLink } from 'types';
import { ARTICLE_PAGE_SIZE } from 'lib/constants';
import BrowsePagePagination from 'styled/components/BrowsePagePagination';
import LoadingSkeleton from 'styled/components/LoadingSkeleton';
import getDefaultArticleUrl from 'utils/getDefaultArticleUrl';
import getDefaultDrugPageLink from 'utils/getDefaultDrugPageLink';
import { getMedianDescriptionsLength } from 'utils/getMedianDescriptionsLength';
import { useRouter } from 'next/router';
import type { IDrugLink } from 'state/sanitizers/sanitizeDrugLink';

type Props = {
  articles: (ArticleLink | IDrugLink)[];
  articleCount: number;
  browsePageSlug: string;
  isLoading: boolean;
  pageNumber: number;
  shouldPaginate?: boolean;
};

const ArticleCardTileList: React.FC<Props> = ({
  articles,
  articleCount,
  isLoading,
  shouldPaginate,
  pageNumber,
}) => {
  const { asPath } = useRouter();
  if (!articles.length) return null;
  const medianDescriptionsLength = getMedianDescriptionsLength(
    articles.map(
      (article) => article.moduleDescription || article.subtitle || '',
    ),
  );
  const totalPages = Math.ceil(articleCount / ARTICLE_PAGE_SIZE);
  const hasNextPage = pageNumber < totalPages;
  const hasPrevPage = pageNumber !== 1;
  const prevPage = pageNumber - 1;
  const hrefPath = asPath.split('?')[0];

  return (
    <div className="pb-10">
      {shouldPaginate && (
        <Head>
          {hasPrevPage && (
            <link
              rel="prev"
              href={`${hrefPath}${prevPage === 1 ? '' : `?page=${prevPage}`}`}
            />
          )}
          {hasNextPage && (
            <link rel="next" href={`${hrefPath}?page=${pageNumber + 1}`} />
          )}
        </Head>
      )}
      <BaseCardTileList>
        {articles.map((article) => {
          const articleIsDrugPage = 'isDrugLink' in article;

          return isLoading ? (
            <LoadingSkeleton />
          ) : (
            <ArticleCardTile
              key={article.id}
              title={article.title || ''}
              image={article.moduleImage}
              imageBgColor={article.heroColor}
              description={article.moduleDescription || article.subtitle || ''}
              url={
                article.slug &&
                (articleIsDrugPage
                  ? getDefaultDrugPageLink(article.slug)
                  : getDefaultArticleUrl({
                      slug: article.slug,
                      language: article.language,
                      articleType: article.articleType,
                    }))
              }
              extendedDescription={
                'extendedDescription' in article
                  ? article.extendedDescription
                  : undefined
              }
              medianDescriptionsLength={medianDescriptionsLength}
            />
          );
        })}
      </BaseCardTileList>

      {shouldPaginate && (
        <BrowsePagePagination pageNumber={pageNumber} totalPages={totalPages} />
      )}
    </div>
  );
};

export default ArticleCardTileList;
